/**
 * snapshotSerializer.ts — Deserialize a decrypted server-side snapshot payload
 * back into Redux state.
 *
 * Snapshot creation (serialization + encryption) happens entirely server-side
 * via POST /snapshots/create. The mobile client only needs to deserialize
 * the decrypted payload into Redux on restore.
 *
 * Payload shape matches what create-snapshot/index.js returns before encryption.
 */

import type { AppDispatch } from '../store';
import {
  SET_SEGMENTS,
  ADD_RESPONSES,
  ADD_VAULT,
  ADD_MESSAGES,
} from '../store/constants';

export interface SnapshotPayload {
  schema_version: number;
  created_at: string;
  project_id: number;
  account_id: string;
  // Project row including blob (vault_salt, medical_profile, survey prefs, etc.)
  plan: Record<string, any>;
  medications: any[];
  contacts: any[];
  notes: any[];
  // Scheduled reminders from the prompts table (category = 'reminder')
  reminders: any[];
  calendar_events: any[];
  // Document metadata only — actual files remain in S3
  documents_index: any[];
  // Per-project survey answers (segments table)
  segments: any[];
  // Aggregated per-block survey form state (block_responses table)
  block_responses: any[];
  // Vault credentials — already AES-256-GCM ciphertext, safe to store
  vault_credentials: any[];
  messages: any[];
}

export const CURRENT_SCHEMA_VERSION = 1;

/**
 * Upgrade an older snapshot payload to the latest schema. This is the seam
 * where structural renames / reshapes are normalized so `deserializeSnapshot`
 * only ever sees the current shape. Additions are already handled implicitly
 * by the `Array.isArray` guards below — only non-additive changes (renames,
 * restructures, splits, merges) require a migration step here.
 *
 * Migrations are pure, idempotent, and ordered: each one takes a payload at
 * version N-1 and produces a payload at version N. Unknown future versions
 * pass through untouched (forward-compat: an older app opening a future
 * snapshot does best-effort rendering rather than failing outright).
 */
export function migrateSnapshotPayload(raw: any): SnapshotPayload {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Snapshot payload is empty or not an object.');
  }
  let payload = { ...raw };
  const startVersion: number = Number(payload.schema_version ?? 1) || 1;
  // Intentionally empty for now — v1 is the only shape in the wild. When v2
  // introduces a rename/restructure, add a case here and bump
  // CURRENT_SCHEMA_VERSION.
  for (let v = startVersion; v < CURRENT_SCHEMA_VERSION; v++) {
    // switch (v) { case 1: payload = upgradeV1toV2(payload); break; ... }
  }
  return payload as SnapshotPayload;
}

/**
 * Rehydrate Redux state from a decrypted snapshot payload.
 * Each slice dispatches the action its reducer expects.
 * Callers should navigate to the project after this completes.
 */
export function deserializeSnapshot(
  dispatch: AppDispatch,
  rawSnapshot: SnapshotPayload | any,
): void {
  const snapshot = migrateSnapshotPayload(rawSnapshot);
  const projectId = String(snapshot.project_id);

  // Project row (includes blob, vault_salt, etc.)
  if (snapshot.plan && Object.keys(snapshot.plan).length) {
    dispatch({ type: 'ADD_PROJECT', payload: { ...snapshot.plan, id: snapshot.project_id } });
  }

  if (Array.isArray(snapshot.medications)) {
    dispatch({ type: 'ADD_MEDICATIONS', payload: { project_id: projectId, list: snapshot.medications } });
  }

  if (Array.isArray(snapshot.contacts)) {
    dispatch({ type: 'ADD_CONTACTS', payload: { project_id: projectId, list: snapshot.contacts } });
  }

  if (Array.isArray(snapshot.notes)) {
    dispatch({ type: 'ADD_NOTES', payload: { project_id: projectId, list: snapshot.notes } });
  }

  if (Array.isArray(snapshot.reminders)) {
    dispatch({ type: 'ADD_REMINDERS', payload: { project_id: projectId, list: snapshot.reminders } });
  }

  // calendarReducer keys events by action.payload.projectId (camelCase)
  if (Array.isArray(snapshot.calendar_events)) {
    dispatch({ type: 'SET_CALENDAR_EVENTS', payload: { projectId, events: snapshot.calendar_events } });
  }

  // Documents index is metadata only — files are still on S3 and load lazily
  if (Array.isArray(snapshot.documents_index)) {
    dispatch({ type: 'ADD_DOCUMENTS', payload: { project_id: projectId, list: snapshot.documents_index } });
  }

  // blocksReducer keys segments by action.payload.projectId (camelCase)
  if (Array.isArray(snapshot.segments)) {
    dispatch({ type: SET_SEGMENTS, payload: { projectId, segments: snapshot.segments } });
  }

  // responsesReducer keys block_responses by action.payload.project_id
  if (Array.isArray(snapshot.block_responses)) {
    dispatch({ type: ADD_RESPONSES, payload: { project_id: projectId, list: snapshot.block_responses } });
  }

  // vaultReducer keys credentials by action.payload.project_id
  if (Array.isArray(snapshot.vault_credentials)) {
    dispatch({ type: ADD_VAULT, payload: { project_id: projectId, list: snapshot.vault_credentials } });
  }

  // messagesReducer keys messages by action.payload.project_id
  if (Array.isArray(snapshot.messages)) {
    dispatch({ type: ADD_MESSAGES, payload: { project_id: projectId, list: snapshot.messages } });
  }
}
